/**
 * Client DTO Type Definitions
 *
 * Simplified, flattened business objects that serve as an intermediate layer
 * between complex API DTOs and display objects. These types are designed to be:
 * - Easy to work with in business logic
 * - Type-safe and predictable
 * - Free from complex nesting
 * - Enhanced with computed properties
 */

import type { EventInput, EventApi } from "@fullcalendar/core";
import type { TherapyColorKey } from "../../../../helpers/slotTherapy";
import {
  AppointmentDto,
  ContinuousAppointmentDto,
  ContinuousParticipantDto,
  ParticipantDto,
  PatientInformingProjectionDto,
  RxDto,
  SublocalityDto,
  WeeklyWorkDayDto,
} from "../../../../api-client";

// =====================================================
// CLIENT DTO INTERFACES (Simplified Business Objects)
// =====================================================

/**
 * Simplified Patient representation for calendar operations
 */
export interface ClientPatient {
  id: number;
  firstName: string;
  lastName: string;
  salutation: string;
  fullName: string; // computed: "firstName lastName"
  displayName: string; // computed: "F.LastName"
}

/**
 * Simplified User/Therapist representation
 */
export interface ClientUser {
  id: string;
  firstName?: string;
  lastName: string;
  fullName: string; // computed
  displayName: string; // computed: "F.LastName" or just lastName
}

/**
 * Simplified Therapy representation
 */
export interface ClientTherapy {
  id: string;
  abbreviation: string;
  duration: number;
  name?: string;
  isHeatTreatment: boolean; // from Therapy.isPassive (adaptTherapyFromApi) — NOT named isPassive on this shape
  maxParticipants?: number; // > 1 marks a group (KGG) therapy
  /**
   * MD-232: the catalog identity this slot's colour is looked up by — the
   * TherapyMethod id for a line item, the PrescribedTherapy id for a term.
   * Distinct from `id` (a tarif-bound line item, e.g. `GKV_KGN BO`) and from
   * `abbreviation`, which is renameable display text and must never key the
   * colour map. Undefined = no curated identity; render the grey fallback.
   */
  colorKey?: TherapyColorKey;
  /**
   * MD-328: what unit this position's price is in.
   *
   * `PER_TIME_INTERVAL` means the stored price is the price of ONE
   * Zeitintervall and the billed quantity comes from how long the slot runs —
   * so for these, and only these, the therapist is allowed to change a
   * treatment's duration. Everything else is `FIXED_PER_SERVICE`, where the
   * duration is the Regelbehandlungszeit and resizing it would change the
   * calendar without changing the bill.
   *
   * Today only the ErgoBlanko catalog (§ 125a Ergotherapie) is per-interval.
   */
  pricingBasis?: string;
}

/** One Zeitintervall, in minutes — § 125a Ergotherapie, Anlage 2. */
export const ZEITINTERVALL_MINUTES = 15;

/**
 * True when this slot's price is per Zeitintervall, i.e. its duration is the
 * billed quantity rather than a fixed Regelbehandlungszeit.
 *
 * The single place that decides whether a treatment's duration may be edited.
 * Reads the catalog flag rather than testing the tarif group or the discipline:
 * `ErgoBlanko` happens to be the only per-interval catalog today, but the DGUV
 * one is a documented candidate to follow, and naming either here would go
 * stale the moment it did.
 */
export const isIntervalPriced = (therapy?: { pricingBasis?: string } | null): boolean =>
  therapy?.pricingBasis === "PER_TIME_INTERVAL";

/**
 * Simplified Address representation
 */
export interface ClientAddress {
  id?: string;
  street?: string;
  city?: string;
  postalCode?: string;
  fullAddress: string; // computed
  /** Facility/business name (e.g. Heim name) from contactInfo, when present */
  businessName?: string;
  sublocalityId?: string;
  sublocality?: SublocalityDto;
}

export type VisitType = "haus" | "praxis" | "heim";

export type TimeSlotType =
  | "TimeSlot"
  | "EventTimeSlot"
  | "GroupTimeSlotPatient"
  | "GroupTimeSlotTherapist"
  | "ContinuousGroupTimeSlotPatient"
  | "ContinuousGroupTimeSlotTherapist"
  | "ContinuousEventTimeSlot"
  | "LeaveOfAbsence"
  | "GoneFishing"
  | "TravelTime"
  | "LunchBreak"
  | "WorkingHours"
  | "CancelledDay"
  | "SalesTimeSlot"
  | "PatientSlot"
  | "DraftPlacement"
  | "DraftShift"
  | "DraftTravelTime"
  | "PublicHoliday";

/** Check if a timeSlotType is any group therapist variant */
export const isGroupTherapistType = (type?: string): boolean =>
  type === "GroupTimeSlotTherapist" || type === "ContinuousGroupTimeSlotTherapist";

/** Check if a timeSlotType is any group patient variant */
export const isGroupPatientType = (type?: string): boolean =>
  type === "GroupTimeSlotPatient" || type === "ContinuousGroupTimeSlotPatient";
/**
 * Simplified TimeSlot for calendar operations
 */
export interface ClientTimeSlot {
  id: string;
  start: Date;
  end: Date;
  type: TimeSlotType;

  // Core relationships
  user?: ClientUser;
  patient?: ClientPatient;
  therapy?: ClientTherapy;
  therapyRxId?: string;
  containsHeatTreatment?: boolean;
  participants?: ParticipantDto[]; // for group time slots

  // Appointment context
  appointmentId?: string;
  treatmentIndex?: string;
  attended?: boolean;
  wasDocumentedCount?: number;
  informing?: PatientInformingProjectionDto | null;

  // Event-specific properties
  title?: string; // for EventTimeSlot, LeaveOfAbsence
  loaType?: string; // for LeaveOfAbsence

  // Status flags
  isCancelled?: boolean;
  hasNotes?: boolean;
  reason?: string;

  rx?: RxDto;
  visitType?: VisitType;
  visitFee?: any;

  sublocality?: SublocalityDto;
  isContinuous: boolean;
  groupAppointmentId?: string;
}

/**
 * Simplified Appointment for calendar operations
 */
export interface ClientAppointment {
  id: string;
  start: Date; // earliest timeSlot start
  end: Date; // latest timeSlot end

  // Core data
  patient: ClientPatient;
  timeSlots: ClientTimeSlot[];
  address?: ClientAddress;
  participants?: ParticipantDto[];

  // Status and metadata
  attended?: boolean;
  frequency?: object; // kept as-is for now
  hasMultipleTimeSlots: boolean; // computed

  // Display helpers
  displayTitle: string; // computed: "F.LastName"
  therapyAbbreviations: string[]; // computed from timeSlots
  containsHeatTreatment: boolean; // computed
  hasNotes: boolean; // computed
  isCancelled: boolean; // computed

  reason?: string;
}

export interface ClientContinuousAppointment {
  id: string;
  start: Date; // earliest timeSlot start
  end: Date; // latest timeSlot end

  // Core data
  patient: ClientPatient;
  timeSlots: ClientTimeSlot[];
  address?: ClientAddress;
  participants?: ContinuousParticipantDto[]; // for group continuous appointments (KGG)

  // Status and metadata
  frequency?: object; // kept as-is for now
  hasMultipleTimeSlots: boolean; // computed

  // Display helpers
  displayTitle: string; // computed: "F.LastName"
  therapyAbbreviations: string[]; // computed from timeSlots
  containsHeatTreatment: boolean; // computed
  hasNotes: boolean; // computed
  isCancelled: boolean; // computed
}

export interface ClientLunchBreak {
  id: string; // Required - generated from slotId or userId+start if not available (for FullCalendar)
  slotId?: string; // Original slotId from API (may be undefined)
  start: Date;
  end: Date;
  userId: string;
  isException: boolean;
  exceptionId?: string;
}

export interface ClientContinuousLunchBreak {
  id: string; // Required - generated from slotId or userId+start if not available (for FullCalendar)
  slotId?: string; // Original slotId from API (may be undefined)
  weeklyWorkDay?: WeeklyWorkDayDto;
  start: Date;
  end: Date;
  startTime: string;
  endTime: string;
  daysOfWeek?: any[];
  userId: string;
}

export interface ClientWorkingHours {
  id: string; // Required - generated from slotId or userId+start if not available (for FullCalendar)
  slotId?: string; // Original slotId from API (may be undefined)
  start: Date;
  end: Date;
  userId: string;
  isException: boolean;
  exceptionId?: string;
}

// =====================================================
// FULLCALENDAR INTEGRATION TYPES
// =====================================================

export type EventType = "appointment" | "timeslot" | "availability" | "working-hours" | "draft";

/**
 * Our custom data that goes in extendedProps
 */
export interface CalendarEventExtendedProps {
  // Classification
  eventType: EventType;

  // Source references
  appointmentId?: string;
  timeSlotId?: string;

  // Display metadata
  multipleTimeSlots?: boolean;
  containsHeatTreatment?: boolean;
  hasNotes?: boolean;
  isCancelled?: boolean;

  // Event-specific properties (for non-appointment events)
  timeSlotType: TimeSlotType;
  loaType?: string;
  source?: string; // Source of the event (e.g., "public-holiday" for public holidays)
  attended?: boolean;
  informing?: PatientInformingProjectionDto | null;

  // Extended properties for tooltips/context
  patient?: ClientPatient;
  therapy?: ClientTherapy;
  user?: ClientUser;
  address?: ClientAddress;

  rx?: RxDto;
  therapyRxId?: string;

  treatmentIndex?: string;
  wasDocumentedCount?: number;
  reason?: string;

  visitType?: VisitType;
  visitFee?: any;

  sublocality?: SublocalityDto;
  display?: string;

  isContinuous: boolean;
  isException?: boolean; // for LunchBreaks
  exceptionId?: string; // for LunchBreaks
  slotId?: string; // Original API slotId for LunchBreaks (may be undefined)
  weeklyWorkDay?: WeeklyWorkDayDto; // for ContinuousLunchBreaks
  groupAppointmentId?: string;
  participants?: ParticipantDto[]; // for group time slots
  isOutsideWorkingHours?: boolean; // Appointment scheduled outside working hours (main calendar mode)

  /**
   * "Umziehen → Nur diese": marks a move that lifts ONLY this slot onto the
   * pinboard (client-side slot pin) instead of the whole appointment.
   */
  moveSingleSlot?: boolean;

  // Draft event properties (scheduler agent shift/placement previews)
  originalStart?: string;
  originalEnd?: string;
  originalTherapistId?: string;
  originalTherapistName?: string;
  /** @deprecated use draftId */
  unscheduledAppointmentId?: string;
  draftId?: string;
  drivingTimeAction?: "create" | "update" | "delete";
}

/**
 * Strongly typed FullCalendar event with our custom extendedProps
 */
export type CalendarEvent = Omit<EventInput, "extendedProps"> & {
  extendedProps: CalendarEventExtendedProps;
};

export type CalendarBusinessHours = {
  daysOfWeek?: any[];
  startTime: string;
  endTime: string;
}[];

export interface CalendarResourceExtendedProps {
  userId?: string;
  deltaMinutes?: number; // Over/under hours for the selected day (timesheet mode)
  yearDeltaMinutes?: number; // Over/under hours for the full calendar year (timesheet mode)
  warningCount?: number; // Count of appointments outside working hours (main calendar mode)
}

export type CalendarResource = {
  id: string;
  title: string;
  businessHours?: CalendarBusinessHours;
  extendedProps: CalendarResourceExtendedProps;
};

/**
 * Helper type for working with EventApi in callbacks
 */
export type CalendarEventApi = EventApi & {
  extendedProps: CalendarEventExtendedProps;
};

/**
 * @deprecated Use CalendarEvent instead
 * Legacy flat interface kept for migration purposes
 */
export interface LegacyCalendarEvent {
  id: string;
  title: string;
  start: string; // ISO string for FullCalendar
  end: string; // ISO string for FullCalendar

  // FullCalendar specific
  resourceId?: string;
  groupId?: string;
  allDay?: boolean;
  editable?: boolean;
  resizable?: boolean;
  color?: string;
  classNames?: string[];

  // Classification
  eventType: "appointment" | "timeslot" | "availability" | "working-hours";

  // Source references
  appointmentId?: string;
  timeSlotId?: string;

  // Display metadata
  multipleTimeSlots?: boolean;
  containsHeatTreatment?: boolean;
  hasNotes?: boolean;
  isCancelled?: boolean;

  // Event-specific properties (for non-appointment events)
  type?: string;
  loaType?: string;
  attended?: boolean;

  // Extended properties for tooltips/context
  patient?: ClientPatient;
  therapy?: ClientTherapy;
  user?: ClientUser;
  address?: ClientAddress;
}

// =====================================================
// FULLCALENDAR HELPER FUNCTIONS
// =====================================================

/**
 * Type-safe helper to extract our custom data from EventApi
 */
export const getEventExtendedProps = (event: EventApi): CalendarEventExtendedProps => {
  return event.extendedProps as CalendarEventExtendedProps;
};

/**
 * Type-safe helper to extract ClientTimeSlot data from EventApi
 */
export const getEventClientTimeSlot = (event: EventApi): ClientTimeSlot | null => {
  const props = getEventExtendedProps(event);

  if (!props.timeSlotId) return null;

  return {
    id: props.timeSlotId,
    start: new Date(event.start!),
    end: new Date(event.end!),
    type: (props.timeSlotType as any) || "TimeSlot",
    appointmentId: props.appointmentId,
    user: props.user,
    patient: props.patient,
    therapy: props.therapy,
    attended: props.attended,
    isCancelled: props.isCancelled,
    hasNotes: props.hasNotes,
    reason: props.reason,
    isContinuous: props.isContinuous,
  };
};

// =====================================================
// CONFIGURATION TYPES
// =====================================================

/**
 * Options for appointment to calendar event transformation
 */
export interface AppointmentTransformOptions {
  groupCheck?: boolean;
  lockedAppointments?: string[];
  sublocalities?: SublocalityDto[];
  isBackgroundEvent?: boolean;
}

/**
 * Options for timeslot to calendar event transformation
 */
export interface TimeslotTransformOptions {
  lockedAppointments?: string[];
  sublocalities?: SublocalityDto[];
  isBackgroundEvent?: boolean;
}

/**
 * Batch timeslot data structure
 */
export interface TimeslotBatchData {
  goneFishings?: any[];
  leaveOfAbsences?: any[];
  eventTimeSlots?: any[];
  continuousEventTimeSlots?: any[];
  lunchBreaks?: any[];
  travelTimes?: any[];
  simpleWorkingHours?: any[];
  patientSlots?: any[];
}

// =====================================================
// UTILITY TYPES
// =====================================================

/**
 * Partial update data for timeslots
 */
export interface TimeSlotUpdateData {
  id?: string;
  start?: Date;
  end?: Date;
  appointmentId?: string;
  userId?: string;
}

/**
 * Analytics computed from client appointments
 */
export interface AppointmentAnalytics {
  totalAppointments: number;
  multipleSlotCount: number;
  heatTreatmentCount: number;
  cancelledCount: number;
  therapyBreakdown: Record<string, number>;
  uniquePatients: number;
}

/**
 * Context menu action types
 */
export type ContextActionKind = "WaitList" | "ShelfEvent";

export interface ContextActionBaseEvent {
  id: string; // derived id (not persisted)
  kind: ContextActionKind; // discriminant for action
  start: string; // ISO start (slot clicked)
  resourceId?: string;
}

export interface WaitListContextActionEvent extends ContextActionBaseEvent {
  kind: "WaitList";
}

export interface ShelfContextActionEvent extends ContextActionBaseEvent {
  kind: "ShelfEvent";
}

export type ContextActionEvent = WaitListContextActionEvent | ShelfContextActionEvent;
